import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSetup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [status, setStatus] = useState('checking'); // 'checking', 'available', 'completed', 'error'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    setup_key: ''
  });

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/status`);
      if (response.data.admin_exists) {
        setStatus('completed');
      } else {
        setStatus('available');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/setup/admin`, {
        email: formData.email,
        name: formData.name,
        password: formData.password,
        setup_key: formData.setup_key
      });

      toast({
        title: "Success!",
        description: response.data.message,
      });

      setStatus('completed');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error) {
      toast({
        title: "Setup Failed",
        description: error.response?.data?.detail || "Failed to create admin account",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-32 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_75f4a617-7223-46ab-b5af-9fb0ce34d58c/artifacts/s7ixbz5d_customcolor_logo_transparent_background.png" 
            alt="Exciting.Travel" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <h1 className="text-3xl font-black text-white mb-2">Admin Setup</h1>
          <p className="text-gray-400">First-time administrator account creation</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {status === 'checking' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-[#84bc00] animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Checking setup status...</p>
            </div>
          )}

          {status === 'completed' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-[#84bc00] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Setup Complete</h2>
              <p className="text-gray-600 mb-6">
                An admin account already exists. You can log in with your admin credentials.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold"
              >
                Go to Login
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Connection Error</h2>
              <p className="text-gray-600 mb-6">
                Could not connect to the server. Please try again.
              </p>
              <Button onClick={checkStatus} variant="outline">
                Retry
              </Button>
            </div>
          )}

          {status === 'available' && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-amber-800">One-Time Setup</h3>
                    <p className="text-sm text-amber-700">
                      This page creates the first admin account. It will be disabled after setup.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="font-bold">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your Name"
                    className="border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-bold">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="admin@exciting.travel"
                    className="border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="font-bold">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="••••••••"
                    className="border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="font-bold">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    required
                    minLength={6}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    placeholder="••••••••"
                    className="border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="setup_key" className="font-bold">Setup Key</Label>
                  <Input
                    id="setup_key"
                    type="password"
                    required
                    value={formData.setup_key}
                    onChange={(e) => setFormData({...formData, setup_key: e.target.value})}
                    placeholder="Enter the setup key"
                    className="border-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The setup key was provided by your developer.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold py-6 text-lg mt-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Admin...
                    </>
                  ) : (
                    'Create Admin Account'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSetup;
